import React, { useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';

const DataTable = ({
  columns = [],
  rows = [],
  rowCount = 0,
  pageSizeOptions = [5, 10, 25, 50, 100],
  paginationMode = 'client',
  enableRowSelection = false,
  page = 1,
  pageSize = 10,
  loading = false,
  onSortModelChange = () => {},
  onPageChange = () => {},
  onFilterModelChange = () => {},
  ...props
}) => {
  const getRowId = useCallback((row) => {
    if (row.id !== undefined && row.id !== null) return row.id;
    return (
      row._generatedId ||
      (row._generatedId = btoa(encodeURIComponent(JSON.stringify(row))).slice(
        0,
        12,
      ))
    );
  }, []);

  return (
    <DataGrid
      getRowId={(row) => getRowId(row)}
      rows={rows}
      columns={columns}
      {...(paginationMode === 'server' && { rowCount })}
      pagination
      paginationMode={paginationMode}
      sortingMode={paginationMode}
      filterMode={paginationMode}
      paginationModel={{ pageSize: pageSize, page: page }}
      onPaginationModelChange={onPageChange}
      onSortModelChange={(model) => onSortModelChange(model)}
      onFilterModelChange={onFilterModelChange}
      loading={loading}
      pageSizeOptions={pageSizeOptions}
      headerHeight={40}
      rowHeight={48}
      disableRowSelectionOnClick={!enableRowSelection}
      sx={{
        borderTop: '0px',
        marginTop: 2,
        // 1. Target the footer container specifically to isolate the scope
        '& .MuiDataGrid-footerContainer': {
          // 2. Now target the icons ONLY within the pagination actions
          '& .MuiTablePagination-actions .MuiIconButton-root': {
            border: '1px solid',
            padding: 0,
            // borderRadius: '4px',
            margin: '0 4px',
            color: '#3f5eb5',

            // Optional: Change icon size without affecting others
            '& .MuiSvgIcon-root': {
              fontSize: '2rem',
            },
          },

          // 3. Handle the disabled state strictly in the footer
          '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
            opacity: 0.3,
            color: 'grey',
            border: '1px solid #e0e0e0',
          },
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
          {
            fontWeight: 'bold', // This targets the actual text labels
          },
        '& .MuiTablePagination-selectLabel': {
          display: 'block',
        },

        '& .MuiTablePagination-select': {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            background: '#3f5eb5',
          },
          // border: '0.5px solid #3f5eb5',
          background: 'white',
          borderRadius: '100px',
        },
        '& .MuiTablePagination-toolbar': {
          display: 'flex',
          justifyContent: 'end',
          flexWrap: 'wrap',
          marginLeft: 'auto',
          marginRight: 8,

          fontWeight: 'bold',
          backgroundColor: '',
        },
        border: 'none',
        background: '#EFF6FF',
        boxShadow: 'none',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 16,
        //height: '64vh',
        maxHeight: '84vh',
        '.MuiDataGrid-overlayWrapper': {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)',
        },
        '.MuiDataGrid-columnHeader': {
          backgroundColor: '#f2f4f8',
          color: '#22223b',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          fontSize: 16,
        },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          color: '#3f51b5',
          fontFamily: 'Poppins, sans-serif',
          fontSize: 16,
        },
        '.MuiDataGrid-virtualScroller': {
          minHeight: 200,
          maxHeight: 550,
          overflow: 'auto',
        },
        '.MuiDataGrid-cell': {
          minHeight: 46,
          maxHeight: 46,
          display: 'flex',
          alignItems: 'center',
          color: '#272727',
          fontSize: 16,
        },
        '.MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '.MuiTablePagination-selectLabel': {
          marginBottom: 0,
        },
        '.MuiTablePagination-displayedRows': {
          marginBottom: 0,
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: '#ffffff',
        },
        '& .MuiDataGrid-row': {
          borderBottom: '1px solid #e9edf4',
          bgcolor: 'white',
        },
      }}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: pageSize,
            page: page,
          },
          ...(paginationMode === 'server' && { rowCount }),
        },
        sorting: {
          sortModel: [],
        },
      }}
      {...props}
    />
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  fetchData: PropTypes.func,
  rows: PropTypes.array,
  rowCount: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  enableRowSelection: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  sortModel: PropTypes.array,
  filterModel: PropTypes.object,
  loading: PropTypes.bool,
  onSortModelChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onFilterModelChange: PropTypes.func,
  paginationMode: PropTypes.oneOf(['server', 'client']),
};

export default DataTable;
